import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { MdDeleteOutline } from 'react-icons/md';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  useAvailableProducts,
  AvailableProduct,
  ProductAllocation,
  ProductAllocationSku,
} from '@hooks';
import { pickWarehouseWithStock } from '@utils';
import { Input } from '../../tailwindComponents/Input';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import { AvailableWarehouse, PreSelectedSku } from './types';
import {
  ItemDetailsPanel,
  productToDetails,
  preSelectedToProductDetails,
} from './ItemDetailsPanel';

function unitNounFor(unitOfMeasure: string): string {
  return unitOfMeasure && unitOfMeasure.toLowerCase() !== 'other'
    ? unitOfMeasure
    : 'SKU';
}

function pluralise(count: number, noun: string): string {
  return count === 1 ? noun : `${noun}s`;
}

function SkuBreakdownLine({ sku }: { sku: ProductAllocationSku }) {
  const unitNoun = unitNounFor(sku.unitOfMeasure);
  const showProducts = sku.productsPerSku > 1;

  return (
    <div className='border-t border-gray-100 pt-3 pb-1'>
      <div className='flex justify-between items-start mb-1'>
        <span className='text-sm font-medium text-gray-900'>{sku.name}</span>
        <span className='text-xs text-gray-500'>{sku.skuIdentifier}</span>
      </div>
      <p className='text-sm text-gray-700'>
        <span className='font-medium'>
          {sku.wholeUnitsToPick} {pluralise(sku.wholeUnitsToPick, unitNoun)}
        </span>{' '}
        whole
        {sku.unitsToBreak > 0 && (
          <>
            {' '}
            + crack <span className='font-medium'>
              {sku.unitsToBreak}
            </span> → <span className='font-medium'>{sku.looseToTake}</span>{' '}
            loose
          </>
        )}
      </p>
      {showProducts && (
        <p className='text-xs text-gray-500 mt-0.5'>
          {sku.productsToShip} products / {sku.productsPerSku} per{' '}
          {unitNoun.toLowerCase()}
        </p>
      )}
    </div>
  );
}

export function ProductFieldRow({
  index,
  control,
  removeProduct,
  formState,
  setValue,
  warehouseId,
  preview,
  previewLoading,
  previewError,
  selectedProductIds,
  initialProductOption,
  availableWarehouseOptions,
  setWarehouseAddress,
  showDetailsPanel,
}: {
  index: number;
  control: any;
  removeProduct: (index: number) => void;
  formState: any;
  setValue: any;
  warehouseId?: string;
  preview?: ProductAllocation | null;
  previewLoading?: boolean;
  previewError?: string | null;
  selectedProductIds?: string[];
  initialProductOption?: PreSelectedSku;
  availableWarehouseOptions?: AvailableWarehouse[];
  setWarehouseAddress?: (address: string | null) => void;
  showDetailsPanel?: boolean;
}) {
  const { availableProducts, loadingAvailableProducts, setProductsQuery } =
    useAvailableProducts();

  const [selectedProductRaw, setSelectedProductRaw] = useState<
    AvailableProduct | PreSelectedSku | undefined
  >(initialProductOption);
  const didAutoSelectWarehouse = useRef(false);

  useEffect(() => {
    if (didAutoSelectWarehouse.current) return;
    if (!initialProductOption || !availableWarehouseOptions?.length) return;
    if (warehouseId) return;

    const warehouse = pickWarehouseWithStock(
      initialProductOption.stockByWarehouse,
      availableWarehouseOptions
    );
    if (warehouse) {
      setValue('warehouseId', warehouse.warehouseId);
      setValue('warehouseName', warehouse.name);
      setWarehouseAddress?.(warehouse.address);
      didAutoSelectWarehouse.current = true;
    }
  }, [
    availableWarehouseOptions,
    initialProductOption,
    warehouseId,
    setValue,
    setWarehouseAddress,
  ]);

  const watchedProductId = useWatch({
    control,
    name: `productFields.${index}.productId`,
  });
  const watchedProductQuantity = useWatch({
    control,
    name: `productFields.${index}.productQuantity`,
  });

  const requestedQuantity = useMemo(() => {
    const parsed = Number(watchedProductQuantity);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [watchedProductQuantity]);

  const [showDetails, setShowDetails] = useState(false);

  const productDetails = useMemo(() => {
    if (!showDetailsPanel || !selectedProductRaw) return null;
    if ('skus' in selectedProductRaw) {
      return productToDetails(selectedProductRaw, warehouseId);
    }
    return preSelectedToProductDetails(selectedProductRaw, warehouseId);
  }, [showDetailsPanel, selectedProductRaw, warehouseId]);

  const renderPreview = () => {
    if (!warehouseId) {
      return (
        <p className='text-sm text-gray-400'>
          Select a warehouse to preview the breakdown.
        </p>
      );
    }
    if (!watchedProductId) {
      return (
        <p className='text-sm text-gray-400'>
          Select a product to preview the breakdown.
        </p>
      );
    }
    if (!requestedQuantity) {
      return (
        <p className='text-sm text-gray-400'>
          Enter a quantity to preview the breakdown.
        </p>
      );
    }
    if (!preview) {
      if (previewLoading) {
        return <p className='text-sm text-gray-500'>Calculating breakdown…</p>;
      }
      if (previewError) {
        return <p className='text-sm text-red-600'>{previewError}</p>;
      }
      return (
        <p className='text-sm text-gray-400'>
          No breakdown available for this product.
        </p>
      );
    }

    return (
      <>
        <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-1'>
          <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
            {preview.hasEnoughStock ? (
              <span className='inline-flex items-center gap-1 font-medium text-green-700'>
                <CheckCircleIcon className='h-4 w-4' />
                Enough stock
              </span>
            ) : (
              <span className='inline-flex items-center gap-1 font-medium text-amber-700'>
                <ExclamationCircleIcon className='h-4 w-4' />
                Not enough stock
              </span>
            )}
            <span className='text-gray-300'>·</span>
            <span className='text-gray-700'>
              {preview.totalProductsShipped} products ·{' '}
              {preview.totalWholeUnitsToPick} whole
              {preview.totalLooseCreated > 0 &&
                ` · ${preview.totalLooseCreated} loose`}
            </span>
          </div>
          <button
            type='button'
            className='text-sm font-medium text-primary hover:underline'
            onClick={() => setShowDetails((value) => !value)}
            aria-expanded={showDetails}
          >
            {showDetails ? 'Hide details' : 'Details'}
          </button>
        </div>

        {showDetails && (
          <div className='mt-3'>
            {preview.skus.map((sku) => (
              <SkuBreakdownLine key={sku.skuId} sku={sku} />
            ))}
            {!preview.hasEnoughStock && preview.error && (
              <p className='mt-2 text-sm text-amber-700'>{preview.error}</p>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className='flex flex-col gap-4 border border-gray-200 rounded-lg p-4'>
      <div className='flex justify-between items-center'>
        <span className='inline-flex items-center rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700'>
          Product
        </span>
        <button
          type='button'
          className='text-red-600 hover:text-red-800 flex items-center'
          onClick={() => removeProduct(index)}
          aria-label={`Remove product ${index + 1}`}
        >
          <MdDeleteOutline size={20} className='mr-1 -mt-0.5' />
          Delete
        </button>
      </div>

      <div className='flex flex-col md:flex-row md:flex-wrap md:items-start gap-4'>
        <div className='md:flex-1 md:min-w-[14rem]'>
          <Controller
            name={`productFields.${index}.productName`}
            control={control}
            render={({ field }) => (
              <Autocomplete
                label='Product'
                options={availableProducts
                  .filter(
                    (product) =>
                      product.productId === watchedProductId ||
                      !(selectedProductIds ?? []).includes(product.productId)
                  )
                  .map((product) => ({
                    id: product.productId,
                    label: product.name,
                    value: product.productId,
                  }))}
                loading={loadingAvailableProducts}
                placeholder='Start typing to search products...'
                value={field.value}
                onInputChange={(inputValue) => {
                  field.onChange(inputValue);
                  setProductsQuery(inputValue || '');
                }}
                onChange={(selected) => {
                  if (!selected) return;
                  setValue(`productFields.${index}.productId`, selected.value, {
                    shouldValidate: true,
                  });
                  field.onChange(selected.label);
                  setSelectedProductRaw(
                    availableProducts.find(
                      (product) => product.productId === selected.value
                    )
                  );
                }}
                isInvalid={!!formState.errors.productFields?.[index]?.productId}
                validationMessage={
                  formState.errors.productFields?.[index]?.productId
                    ?.message as string
                }
              />
            )}
          />
        </div>
        <div className='md:w-44'>
          <Controller
            name={`productFields.${index}.productQuantity`}
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Number of products'
                type='number'
                min={1}
                isInvalid={
                  !!formState.errors.productFields?.[index]?.productQuantity
                }
                validationMessage={
                  formState.errors.productFields?.[index]?.productQuantity
                    ?.message as string
                }
              />
            )}
          />
        </div>
      </div>

      <div className='flex flex-col gap-4 lg:flex-row lg:items-stretch'>
        {productDetails && (
          <ItemDetailsPanel
            details={productDetails}
            className='lg:flex-[2] lg:min-w-0'
          />
        )}
        <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 lg:flex-1 lg:min-w-0'>
          <p className='text-xs font-medium text-gray-500 mb-2'>
            Allocation preview
          </p>
          {renderPreview()}
        </div>
      </div>
    </div>
  );
}
